/**
 * Input Component
 * Consistent input field with label and helper text
 * 
 * @author Varun Pandey
 */

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../utils/cn';
import { PasswordStrength } from './PasswordStrength';
import { Loader } from './Loader';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** When true, error ring is still applied but error message is not rendered (e.g. when showing errors via toast). */
  hideErrorText?: boolean;
  showPasswordToggle?: boolean;
  showPasswordStrength?: boolean;
  isLoading?: boolean;
  /** Optional trailing control (e.g. edit icon) shown on the right inside the field. */
  trailingAction?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hideErrorText = true,
      type = 'text',
      showPasswordToggle = false,
      showPasswordStrength = false,
      isLoading = false,
      trailingAction,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    // When showPassword is true, show text (password visible). When false, show password (hidden)
    const inputType = isPassword && showPasswordToggle ? (showPassword ? 'text' : 'password') : type;
    const passwordValue = typeof value === 'string' ? value : '';
    const [showError, setShowError] = useState<boolean>(Boolean(error));
    const hasTrailing = Boolean(trailingAction) || isLoading || (isPassword && showPasswordToggle);

    // Whenever a new error comes from parent, show the red outline again
    useEffect(() => {
      if (error) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }, [error]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Clear visual error as soon as user modifies the value
      if (showError) {
        setShowError(false);
      }
      onChange?.(e);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="mb-2 block text-xs font-medium text-[#8E8E93] uppercase tracking-wider">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          <input
            type={inputType}
            value={value}
            className={cn(
              'flex h-12 w-full rounded-[0.75rem] bg-transparent border border-[#3A3A3C] px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] disabled:cursor-not-allowed disabled:opacity-50',
              showError && error && 'ring-2 ring-destructive',
              hasTrailing ? 'pr-12' : '',
              className
            )}
            ref={ref}
            onChange={handleChange}
            {...props}
          />
          {isLoading ? (
            <div className="absolute right-4 left-auto flex items-center justify-center h-12">
              <Loader size="small" />
            </div>
          ) : trailingAction ? (
            <div className="absolute right-4 left-auto flex items-center justify-center h-12">
              {trailingAction}
            </div>
          ) : isPassword && showPasswordToggle ? (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 left-auto flex items-center justify-center h-12 text-[#8E8E93] hover:text-white transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          ) : null}
        </div>
        {isPassword && showPasswordStrength && passwordValue && (
          <div className="mt-2">
            <PasswordStrength password={passwordValue} />
          </div>
        )}
        {error && showError && !hideErrorText && (
          <p className="mt-1 text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
